import React, {useContext, useEffect, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import svgPanZoom from 'svg-pan-zoom'
import { SVG_LINK, SVG_X_LINK } from './constants'
import { ConnectorViewMappingContext } from './context/context'

const Loader = () => {
  const intl = useIntl()
  return (
    <div className="connector-mapping-svg-loader">
      {intl.formatMessage({ id: 'connectorViews.mapping.loading' })}
    </div>
  )
}

const isValidSvgUrl = (url) => {
  if (!url || url.startsWith('null')) {
    return false
  }

  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

const backgroundImageValidator = (props, propName, componentName) => {
  if (isValidSvgUrl(props.src) && !props.backgroundImage) {
    return new Error(
      `${componentName}: \`backgroundImage\` is required.`
    )
  }

  return null
}

const SvgViewer = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(ConnectorViewMappingContext)
  const panzoom = useRef(null)
  const container = useRef(null)
  const svgElRef = useRef(null)
  const svgHolderRef = useRef(null)
  const [svgLoadState, setSvgLoadState] = useState({ loading: true, hasError: false })

  useEffect(() => {
    return () => {
      if (panzoom.current) panzoom.current.destroy()
    }
  }, [])

  const srcIsInvalid = !props.src || props.src.includes('/null')
  const src = srcIsInvalid ? '' : props.src

  const onSvgElementBind = (el) => {
    if (!el || el === svgElRef.current) {
      return
    }
    svgElRef.current = el

    const relSize = relSizeCalc(el)
    props.setFontSize(relSize / 2)

    el.querySelectorAll('image')

    let isFirstLoad = el.querySelectorAll('image')?.length === 0

    if (!state?.displayPng) {
      el.querySelectorAll('image').forEach((element) => {
        element.setAttribute('visibility', 'hidden')
      })
    } else {
      el.querySelectorAll('image').forEach((element) => {
        element.setAttribute('visibility', 'visible')
      })
    }

    if (isFirstLoad) {
      el &&
        el.querySelectorAll('text').forEach((f) => {
          initTextNode(f, relSize)
          initCircle(f.parentElement.querySelector('circle'), relSize)
          elemIsMapped(f.parentElement) && markElementAsMapped(f.parentElement)
          if (f.parentElement.getAttribute('id') in state.mappedReferences) {
            initConnectorTextNode(f.parentElement, relSize)
          }

          f.parentElement
            .querySelectorAll('*:not(text)')
            .forEach((f) =>
              elemIsMapped(f.parentElement)
                ? initMappedElement(f, relSize)
                : dataInitAttrs(f, relSize)
            )

          f.addEventListener('click', (e) => {
            textOnClick(e.target.parentElement, el)
          })
          f.addEventListener('mouseover', (e) => {
            textMouseOver(e.target.parentElement)
          })
          f.addEventListener('mouseout', (e) =>
            textMouseOut(e.target.parentElement)
          )
        })
    }
  }

  const relSizeCalc = (svg) =>
    (svg.viewBox.animVal.width + svg.viewBox.animVal.height) / 200

  const initTextNode = (text, relSize) => {
    let posElement = text.parentElement.querySelector('polyline')
    let x, y

    if (posElement) {
      const points = posElement.points
      x = points[0].x
      y = points[1].y
    }

    if (!posElement) {
      posElement = text.parentElement.querySelector('line')

      if (posElement) {
        x = posElement.x1.animVal.value
        y = posElement.y1.animVal.value
      }
    }

    if (posElement) {
      text.setAttribute('x', x + relSize)
      text.setAttribute('y', y + relSize)
    }

    text.textContent = `${text.parentElement.id}`
  }

  const initConnectorTextNode = (foreignObject, relSize) => {
    let posElement = foreignObject.querySelector('polyline')
    let x, y

    if (posElement) {
      const points = posElement.points
      x = points[0].x
      y = points[1].y
    }

    if (!posElement) {
      posElement = foreignObject.querySelector('line')

      if (posElement) {
        x = posElement.x1.animVal.value
        y = posElement.y1.animVal.value
      }
    }
    if (posElement) {
      const ns = 'http://www.w3.org/2000/svg'
      const tempTextObject = document.createElementNS(ns, 'text')
      tempTextObject.setAttribute('x', x + relSize)
      tempTextObject.setAttribute('y', y + relSize + 20)
      tempTextObject.setAttribute('id', `connector-${x}`)
      tempTextObject.setAttribute('class', 'connectors-mapping-data')
      tempTextObject.textContent = `${state.mappedReferences[foreignObject.getAttribute('id')]}`

      tempTextObject.addEventListener('mouseover', (e) => {
        let element = e.target.parentElement
        element.setAttribute(
          'class',
          `${element.getAttribute('class')} svg-connector-info`
        )
        let tempClass = foreignObject.getAttribute('class')
        foreignObject.setAttribute('class', `${tempClass} hlOn`)
      })
      tempTextObject.addEventListener('mouseout', (e) => {
        let element = e.target.parentElement
        let tempClass = element.getAttribute('class')
        element.setAttribute(
          'class',
          tempClass.replace(/svg-connector-info/, '')
        )
        foreignObject.setAttribute(
          'class',
          foreignObject.getAttribute('class')?.replace(/hlOn/, '')
        )
      })
      foreignObject.appendChild(tempTextObject)
    }
  }

  const initCircle = (circle, relSize) => {
    let posElement = [...circle.parentElement.querySelectorAll('polyline')]
    let x, y, radius

    if (posElement) {
      const points = [].concat.apply(
        [],
        posElement.map((el) => parsePoints(el))
      )
      const xPoints = points.map((pt) => pt[0])
      const yPoints = points.map((pt) => pt[1])
      const maxX = Math.max(...xPoints)
      const minX = Math.min(...xPoints)
      const maxY = Math.max(...yPoints)
      const minY = Math.min(...yPoints)

      x = (maxX + minX) / 2
      y = (maxY + minY) / 2
      radius = maxX - minX
    }

    if (!posElement) {
      posElement = circle.parentElement.querySelector('line')

      if (posElement) {
        radius = Math.abs(
          posElement.x2.animVal.value + posElement.x1.animVal.value
        )
        x = radius / 2
        y =
          Math.abs(posElement.y2.animVal.value + posElement.y1.animVal.value) /
          2
      }
    }

    if (posElement && x) {
      circle.setAttribute('cx', x)
    }
    if (posElement && y) {
      circle.setAttribute('cy', y)
    }
    if (posElement && Number.isFinite(radius)) {
      circle.setAttribute('r', radius)
    }
  }

  const parsePoints = (el) => [...el.points].map((pt) => [pt.x, pt.y])

  const initMappedElement = (el, relSize) => {
    dataInitAttrs(el, relSize, '#DAF7A6')
  }

  const textOnClick = (el, svg) => {
    if (elemIsMapped(el)) {
      dispatch({
        type: 'selected_svg_object',
        data: el.id,
      })

      let autoMappedElement = document.getElementById(`auto-mapped-${el.id}`)
      if (autoMappedElement) {
        autoMappedElement.scrollIntoView({ behavior: 'smooth' })
      }

      return
    }

    props.onReferenceSelect(el.id)
  }

  const textMouseOver = (el) => {
    el.setAttribute('class', `${el.getAttribute('class')} hlOn`)
  }

  const textMouseOut = (el) => {
    el.setAttribute('class', el.getAttribute('class').replace(/hlOn/, ''))
  }

  const elemIsMapped = (el) => props.mappedReferences.includes(el.id)

  const markElementAsMapped = (el) => {
    el.setAttribute('class', 'mappedSvgElement')
  }

  const setAttributes = (el, attrs) => {
    for (const key in attrs) {
      el.setAttribute(key, attrs[key])
    }
  }

  const dataInitAttrs = (el, relSize, color = 'yellow') => {
    const newN = el.cloneNode()

    newN.setAttribute('class', 'hl')
    setHlAttrs(newN, relSize, color)
    el.after(newN)
  }

  const setHlAttrs = (el, relSize, color) => {
    setAttributes(el, {
      stroke: highlightValue(el, 'stroke', color),
      fill:
        el.constructor.name === 'SVGCircleElement'
          ? 'none'
          : highlightValue(el, 'fill', color),
      'stroke-width': highlightValue(el, 'stroke-width', relSize * 0.07),
      style: 'opacity: 1',
    })
  }

  const highlightValue = (el, attr, newVal) => {
    return el.getAttribute(attr) === 'none' ? 'none' : newVal
  }

  const svgPreProcessor = (code) => {
    return addCircles(addTextTitles(replaceBackgroundImageUrl(code)))
  }

  const replaceBackgroundImageUrl = (svgSource) =>
    svgSource.replace(
      /xlink:href=["'][^"']*["']/gm,
      `xlink:href="${props.background}"`
    )

  const addTextTitles = (svgSource) =>
    svgSource.replace(/(<g[^>]+?>)([^<]*?)(<[^g>]+?>)/gm, '$1$2<text></text>$3')

  const addCircles = (svgSource) =>
    svgSource.replace(
      /(<g[^>]+?>)([^<]*?)(<[^g>]+?>)/gm,
      '$1$2<circle></circle>$3'
    )

  const onSvgLoadedInDom = () => {
    let svg = getSvgContainer()

    let isFirstLoad = svg.querySelectorAll('image')?.length === 0

    // Append Png image to background of connector mapping view
    if (props?.backgroundImage && isFirstLoad && state?.displayPng) {
      let imageContainer = svg.querySelectorAll('g[id="viewport-*"]')

      let svgBgImage = document.createElementNS(SVG_LINK, 'image')
      svgBgImage.setAttributeNS(SVG_LINK, 'height', '3300')
      svgBgImage.setAttributeNS(SVG_LINK, 'width', '3600')
      svgBgImage.setAttributeNS(SVG_LINK, 'id', 'connector-view-bg-img')
      svgBgImage.setAttributeNS(SVG_X_LINK, 'href', props.backgroundImage)
      svgBgImage.setAttributeNS(SVG_LINK, 'x', '0')
      svgBgImage.setAttributeNS(SVG_LINK, 'y', '0')
      if (imageContainer?.length > 0) {
        imageContainer.forEach((f, index) => {
          if (index === 0) {
            f.insertBefore(svgBgImage, f.childNodes[0])
          }
        })
      } else {
        svg.insertBefore(svgBgImage, svg.childNodes[0])
      }
    }

    if (props.panzoom) {
      initializePanZoom(svg)
    }

    if (props.onReady) {
      props.onReady(svg, panzoom.current)
    }
  }

  const getSvgContainer = () => {
    return container.current.getElementsByTagName('svg')[0]
  }

  const initializePanZoom = (svg) => {
    panzoom.current = svgPanZoom(svg)
  }

  const onError = (e) => {
    console.error('SVG load error:', e)
  }

  useEffect(() => {
    if (!src || !isValidSvgUrl(src)) {
      return
    }

    let cancelled = false
    svgElRef.current = null
    setSvgLoadState({ loading: true, hasError: false })

    // `src` points at the media/CDN domain, not this app's API - use the native fetch API
    // (instead of the shared axios instance) so this cross-origin request never inherits
    // the Authorization header services/base.js's getRequest sets as a global axios default.
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load SVG: ${res.status}`)
        return res.text()
      })
      .then((text) => {
        if (cancelled || !svgHolderRef.current) return

        svgHolderRef.current.innerHTML = svgPreProcessor(text)

        const svgEl = svgHolderRef.current.querySelector('svg')
        if (svgEl) {
          svgEl.setAttribute('id', 'connector-view-svg-image')
          onSvgElementBind(svgEl)
        }

        setSvgLoadState({ loading: false, hasError: false })
        onSvgLoadedInDom()
      })
      .catch((e) => {
        if (cancelled) return
        onError(e)
        setSvgLoadState({ loading: false, hasError: true })
      })

    return () => {
      cancelled = true
    }
  }, [src])

  return (
    <>
      <div className="connector-mapping-svg-container" ref={container}>
        {src && isValidSvgUrl(src) ? (
          <>
            {svgLoadState.loading && <Loader />}
            {!svgLoadState.loading && svgLoadState.hasError && props.errorComponent}
            <div ref={svgHolderRef} style={{ display: 'contents' }} />
          </>
        ) : (
          <div className="connector-mapping-svg-error">
            {intl.formatMessage({ id: 'connectorViews.mapping.svgFailed' })}
          </div>
        )}
      </div>
    </>
  )
}

SvgViewer.propTypes = {
  src: PropTypes.string.isRequired,
  background: PropTypes.string,
  panzoom: PropTypes.bool.isRequired,
  onReady: PropTypes.func.isRequired,
  setFontSize: PropTypes.func.isRequired,
  errorComponent: PropTypes.object.isRequired,
  mappedReferences: PropTypes.array.isRequired,
  onReferenceSelect: PropTypes.func.isRequired,
  // backgroundImage: PropTypes.string.isRequired,
  backgroundImage: backgroundImageValidator,
}

export default SvgViewer
